import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonIcon, IonItem, IonLabel, IonModal } from '@ionic/angular';
import { v4 as guid } from 'uuid';

import { TeamListModel } from '@ninety/mobile/platform/api';

import { TypeAheadMultipleComponent } from '../type-ahead/type-ahead-multiple.component';

import { FindTeamByIdPipe } from './helpers/find-team-by-id.pipe';

@Component({
  selector: 'nty-team-multi-select',
  imports: [TypeAheadMultipleComponent, FindTeamByIdPipe, IonIcon, IonItem, IonLabel, IonModal],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TeamMultiSelectComponent),
      multi: true,
    },
  ],
  template: `
    <ion-item [button]="true" [detail]="false" [id]="uniqueId" class="pill" [class.pill--error]="error" lines="none">
      @if (ionIcon || ntyIcon) {
        <ion-icon src="assets/icons/{{ ntyIcon }}" class="icon" />
      }
      @if (label) {
        <ion-label class="label">{{ label }}</ion-label>
      }
      @if (selectedTeamIds.length <= 0) {
        <ion-label class="selected-team-placeholder terra-body">{{ placeholder }}</ion-label>
      } @else if (selectedTeamIds.length === 1) {
        <ion-label class="selected-team terra-body">{{ (teams | findTeamById: selectedTeamIds[0])?.name }}</ion-label>
      } @else if (this.hasAll && selectedTeamIds.length === teams.length) {
        <ion-label class="selected-team terra-body">All</ion-label>
      } @else {
        <ion-label class="selected-team terra-body">
          {{ (teams | findTeamById: selectedTeamIds[0])?.name }} + {{ selectedTeamIds.length - 1 }}
        </ion-label>
      }
      <ion-icon src="assets/icons/caret-down--regular.svg" class="chevron" />
    </ion-item>

    <ion-modal [trigger]="uniqueId" #modal>
      <ng-template>
        <nty-type-ahead-multiple
          class="ion-page"
          [title]="modalTitle"
          [items]="teams"
          [hasAll]="hasAll"
          [selectedItemIds]="selectedTeamIds || []"
          (selectionChange)="onSelectedTeamsChanged($event)"
          (selectionCancel)="modal.dismiss()" />
      </ng-template>
    </ion-modal>
  `,
  styles: `
    @use 'terra';

    .selected-team-placeholder {
      @include terra.text(body);
      color: terra.color(text, light);
      padding-left: 4px;
    }
    .selected-team {
      @include terra.text(body);
      color: terra.color(text);
      padding-left: 4px;
    }

    .pill {
      @include terra.radius(huge);
      border: 1px solid terra.color(border);
      --background: #{terra.color(background)};

      &--error {
        border-color: terra.color(red);
      }
    }

    .icon {
      margin-right: 4x;
      color: terra.color(text, lightest);
      font-size: 24px;
    }

    .label {
      max-width: 50px;
    }

    .chevron {
      color: terra.color(text, lightest);
      font-size: 24px;
    }
  `,
})
export class TeamMultiSelectComponent implements ControlValueAccessor {
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  /** This is necessary in the event that there are multiple
   *  instances of this component on the same page or nested pages
   *  The modal will not open if the id is the same */
  uniqueId = `select-team-modal-${guid()}`;

  @ViewChild('modal') modal?: IonModal;

  /** If an icon name is provided, it is used as the icon at the left side of the select box. */
  @Input() ionIcon?: string;

  @Input() ntyIcon?: string;

  /** If label text is provided, it is used as a label on the left side of the select box. */
  @Input() label?: string;

  @Input() placeholder?: string;

  @Input() hasAll? = false;

  @Input() error: boolean | undefined = false;

  /** List of teams to populate the modal */
  @Input() teams: TeamListModel[] = [];

  /** Use selectedTeam and selectedTeamIdChange when manually connecting this in a component.
   *  Otherwise use FormControl, FormControlName, or ngModel for data binding. */
  @Input() selectedTeamIds: string[] = [];

  @Input() modalTitle = 'Select Team';

  @Output() selectedTeamsIdChange = new EventEmitter<string[]>();

  // @ts-expect-error - this is a required method for ControlValueAccessor
  onChange: (value?: string[]) => void;

  // @ts-expect-error - this is a required method for ControlValueAccessor
  onTouched: () => void;

  onSelectedTeamsChanged(ids?: string[]) {
    this.selectedTeamsIdChange.emit(ids);
    this.selectedTeamIds = ids ?? [];
    this.modal?.dismiss();

    // If this is being manually data bound, onChange will be undefined
    if (this.onChange) this.onChange(ids);
    if (this.onTouched) this.onTouched();
  }

  writeValue(value: string[]): void {
    if (this.hasAll && value.length === 1 && value[0] === 'all') {
      this.selectedTeamIds = [...(this.teams.map(team => team._id) ?? [])];
      this.changeDetectorRef.detectChanges();
    } else {
      this.selectedTeamIds = value;
    }
  }

  registerOnChange(fn: (value?: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectSingleTeam() {
    return this.teams?.find(user => user._id === this.selectedTeamIds[0]);
  }
}
